const NUMBER_OR_TIME_REGEX = /^([0-9.]+)\*(\-?[0-9]+:[0-5][0-9]?|\-?[0-9.]+)$/;

const valuePresent = (val) => {
  return val !== undefined  && val !== null && val !== '';
};

const checkIfTouchedValue = (touched, id, key, idx = null) => {
  if(touched) {
    return touched.some(t => t.id === id && t.key === key && t?.idx === idx);
  }

  return false;
};

const findChanges = (idx_obj, obj, originalObj = {}) => {
  const changes = [];

  for (const key in obj) {
    const currentValue = obj[key];
    const originalValue = originalObj[key];

    if (typeof currentValue === 'object' && typeof originalValue === 'object') continue;

    if (Array.isArray(currentValue) && Array.isArray(originalValue)) {
      const maxLength = Math.max(currentValue.length, originalValue.length);

      for (let i = 0; i < maxLength; i++) {
        if (currentValue[i] !== originalValue[i]) {
          changes.push({id: idx_obj, key: key, idx: i});
        }
      }
    } else {
      if (currentValue !== originalValue) {
        changes.push({id: idx_obj, key: key, idx: null});
      }
    }
  }

  return changes;
};

const splitAmountAndWage = (value) => {
  const matched = value.match(NUMBER_OR_TIME_REGEX);
  const wage = matched ? matched[1] : '';
  const amount = matched ? matched[2] : '';

  return {
    wage: wage,
    amount: amount,
  };
};

const convertAutomatically = (value, key) => {
  if (Array.isArray(value)) {
    return value.map(item => convertAutomatically(item, key));
  }

  if (typeof value !== 'string') {
    return value;
  }
  value = value.replace(/[Ａ-Ｚａ-ｚ０-９！-～]/g, (match) =>
    String.fromCharCode(match.charCodeAt(0) - 0xfee0)
  );
  if (key.startsWith('puPlus') || key.startsWith('puMinus')) {
    return value.replace(/[a-z]|^0+(?!\.|:|$)|\s/ig, '');
  } else {
    return value;
  }
};

const containingArithmeticOperation = (str) => {
  const reg = /([-+]?[0-9]*\.?[0-9]+[\/\+\-\*])+([-+]?[0-9]*\.?[0-9]+)/;

  return !!str.match(reg);
};

const INT4_MAX = 2147483647;
const INT4_MIN = -2147483648;

const clampToInt4 = (n) => {
  if (n > INT4_MAX) return INT4_MAX;
  if (n < INT4_MIN) return INT4_MIN;
  return n;
};

const  calculatePlusFromPlusS = (value) => {
  // 空欄は金額 0。ここで 0 を返さないと parseFloat('') が NaN になり、
  // 金額欄が NaN 表示に化ける。空欄と「0 と入力した状態」は式 (puPlus*s) 側で
  // 区別する (空欄は '', 0 入力は '0')。
  if (value === undefined || value === null || value === '') return 0;

  const {
    wage,
    amount
  } = splitAmountAndWage(value);

  // 金額を出すので計算用の精度を使う (表示用の 5 桁だと 1 円ずれる, #2692)。
  const formattedAmount = timeToFloatForCalc(amount);

  if (parseFloat(wage) === 0 || parseFloat(formattedAmount) === 0) return 0;
  if (parseFloat(wage) * parseFloat(formattedAmount)) {
    return clampToInt4(Math.round(parseFloat(wage) * parseFloat(formattedAmount)));
  }
  else
    return parseFloat(value);
};

const sanitizeInputValue = (value, isTimeFormat = false) => {
  if(value) {
    if (isTimeFormat) {
      let sanitizedValue = value.replace(/[^0-9*+\-,:]/g, '');
      const parts = sanitizedValue.split(':');

      if (parts.length > 1) {
        sanitizedValue = parts.shift() + ':' + parts.shift();
      }

      return sanitizedValue;
    } else {
      return value.replace(/[^0-9*+\-,\.]/g, '');
    }
  } else{
    return value;
  }
};

const isFloat = (input) => {
  const floatRegex = /^-?\d*(\.\d*)?$|^$/;

  return floatRegex.test(input);
};

const isTime = (input) => {
  const timeRegex = /^(\-?[0-9]*)(\:?)([0-5][0-9]?)?$|^$/;

  return timeRegex.test(input);
};

const isTimeFormula = (input) => {
  const regex =  /^([0-9.]+)\*(\-?[0-9]+:[0-5][0-9]?)$/;

  return regex.test(input);
};

const removeCommas = (value) => {
  if(value) {
    return value.toString().replace(/,/g, '');
  } else{
    return value;
  }
};

// 表示用。桁が多いと入力欄が読めなくなるため 5 桁に丸める。
// 金額計算には使わないこと (#2692)。
const timeToFloat = (timeString) => roundTimeFloat(timeString, 100000);

// 金額計算用。丸め桁数を Ruby の CalculationHelper#time_to_float (round(8)) と
// 揃える。5 桁だと "0:05" のように割り切れない分で積が丸め境界をまたぎ、
// 単価 * 時間 の結果が 1 円ずれる (#2692)。
const timeToFloatForCalc = (timeString) => roundTimeFloat(timeString, 100000000);

const roundTimeFloat = (timeString, scale) => {
  if (typeof timeString === 'string' && timeString.includes(':')) {
    const isNegative = `${timeString}`.includes('-');
    const [hours, minutes] = timeString.split(':').map(Number);
    const floatTime = Math.abs(hours) + minutes / 60;
    const result = Math.round(floatTime * scale) / scale;

    return isNegative ? -result : result;
  } else {
    return timeString;
  }
};

const floatToTime = (floatTime) => {
  if (!isNaN(parseFloat(floatTime)) && isFinite(floatTime)) {
    const isNegative = floatTime < 0;
    const parsedfloatTime = Math.abs(parseFloat(floatTime));
    const hours = Math.trunc(parsedfloatTime);
    const minutes = Math.round((parsedfloatTime - hours) * 60);
    const formattedMinutes = minutes.toString().padStart(2, '0');

    return `${isNegative ? '-' : ''}${hours}:${formattedMinutes}`;
  } else {
    return floatTime;
  }
};

const formatTime = (timeString) => {
  if (typeof timeString === 'string' && timeString.includes(':')) {
    const isNegative = `${timeString}`.includes('-');
    const [hours, minutes] = timeString.split(':');
    const formattedHours = Math.abs(parseInt(hours, 10) || 0);
    const formattedMinutes = minutes.padEnd(2, '0');

    return `${isNegative ? '-' : ''}${formattedHours}:${formattedMinutes}`;
  }

  return floatToTime(timeString);
};

export {
  removeCommas,
  timeToFloat,
  timeToFloatForCalc,
  floatToTime,
  formatTime,
  isFloat,
  isTime,
  isTimeFormula,
  sanitizeInputValue,
  containingArithmeticOperation,
  calculatePlusFromPlusS,
  clampToInt4,
  INT4_MAX,
  INT4_MIN,
  valuePresent,
  checkIfTouchedValue,
  findChanges,
  splitAmountAndWage,
  convertAutomatically,
  NUMBER_OR_TIME_REGEX
};
